import React from 'react';
import { utils, writeFileXLSX } from 'xlsx';
import { Field } from '../../atoms/forms';
import { Product } from '../../atoms/product';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { Translate, translateToString } from '../../styles/global/translate';
import { AlertCall, AlertRes } from '../alert/AlertProvider';
import { BoolCheckbox } from '../forms/PopupField';
import { FlexDiv } from '../products/style';
import { Promotion } from './model';

// Display-name headers, mirroring the import template so an exported file
// lands pre-mapped in the import popup: Nom, Actif, field names, and
// 'Période N - Date de début/fin/Couleur' by index
type ExportHeaders = ReturnType<typeof exportHeaders>;
function exportHeaders(fields: Field[]) {
	const reserved = new Set([
		'Id',
		translateToString('name'),
		translateToString('description'),
		translateToString('active'),
		translateToString('import.sidely_fields.barcode'),
	]);
	const fieldNames = new Map<number, string>();
	for (const field of fields) {
		// a custom field named like a builtin column would collide in the
		// sheet: disambiguate with its id
		fieldNames.set(field.id, reserved.has(field.name) ? `${field.name} (${field.id})` : field.name);
	}
	return {
		id: 'Id',
		name: translateToString('name'),
		description: translateToString('description'),
		active: translateToString('active'),
		productId: `Id (${translateToString('product.product_name')})`,
		barcode: translateToString('import.sidely_fields.barcode'),
		companyId: `Id (${translateToString('ClientCompanies')})`,
		companyName: translateToString('ClientCompanies'),
		period: (index: number, label: string) => `${translateToString('period')} ${index} - ${label}`,
		field: (id: number | string) => fieldNames.get(Number(id)) ?? String(id),
	};
}

// The import reads one promotion per line and only picks the product (or
// company) link off the extra ones, so a line holding just an id and a name
// carries no promotion to re-import. Every line repeats the whole promotion,
// which also makes the sheet readable as a flat table.
function linkedRows(promotion: Promotion, exportCompanies: boolean, exportProducts: boolean, products: Map<number, string>, headers: ExportHeaders, main: object): object[] {
	const rows: object[] = [];
	if (exportProducts) {
		for (const product of promotion.products) {
			rows.push({ ...main, [headers.productId]: product.id, [headers.barcode]: products.get(product.id) });
		}
	}
	if (exportCompanies) {
		for (const company of promotion.companies) {
			rows.push({ ...main, [headers.companyId]: company.id, [headers.companyName]: company.name });
		}
	}
	return rows;
}

function promotionToExportable(
	promotion: Promotion,
	exportCompanies: boolean,
	exportProducts: boolean,
	products: Map<number, string>,
	headers: ExportHeaders
): object[] {
	const mainRow = {};
	mainRow[headers.id] = promotion.id;
	mainRow[headers.name] = promotion.name;
	mainRow[headers.description] = promotion.description;
	mainRow[headers.active] = promotion.is_active;
	if (promotion.fields) {
		for (const [field_id, field_value] of Object.entries(promotion.fields)) {
			mainRow[headers.field(field_id)] = field_value;
		}
	}
	promotion.periods.forEach((period, index) => {
		mainRow[headers.period(index + 1, translateToString('name'))] = period.name;
		mainRow[headers.period(index + 1, translateToString('import.sidely_fields.start_date'))] = period.start;
		mainRow[headers.period(index + 1, translateToString('import.sidely_fields.end_date'))] = period.end;
		mainRow[headers.period(index + 1, translateToString('color'))] = period.color;
		if (period.fields) {
			for (const [field_id, field_value] of Object.entries(period.fields)) {
				mainRow[`${headers.field(field_id)} (${translateToString('period')} ${index + 1})`] = field_value;
			}
		}
	});
	const linked = linkedRows(promotion, exportCompanies, exportProducts, products, headers, mainRow);
	// the bare promotion only when nothing links to it: otherwise it would
	// repeat a line the linked ones already carry in full
	return linked.length > 0 ? linked : [mainRow];
}

export default async function exportPromotions(
	promotions: Promotion[],
	products: Product[],
	alert: AlertCall,
	fields: Field[]
) {
	const alertRes = await alert<ExportAlertResult>({
		title: translateToString('linked_elements'),
		buttonMargin: '0px',
		noButtons: true,
		content: ExportAlert
	});
	if (alertRes.res == AlertRes.Break || !alertRes.data) return;
	const [exportCompanies, exportProducts] = alertRes.data;
	const productMap = products.reduce<Map<number, string>>((acc, product) => {
		if (product.barcode) acc.set(product.id, product.barcode);
		return acc;
	}, new Map());
	const headers = exportHeaders(fields);
	const rows = promotions.reduce<object[]>(
		(acc, p) =>
			acc.concat(
				promotionToExportable(p, exportCompanies, exportProducts, productMap, headers)
			),
		[]
	);
	const ws = utils.json_to_sheet(rows);
	const wb = utils.book_new();
	utils.book_append_sheet(wb, ws, 'Data');
	writeFileXLSX(wb, `export-promotions-${new Date().toISOString()}.xlsx`);
}

type ExportAlertResult = [boolean, boolean];
function ExportAlert(_, onOk: (data: ExportAlertResult) => void) {
	const [exportCompanies, setExportCompanies] = React.useState(false);
	const [exportProducts, setExportProducts] = React.useState(false);

	return (
		<FlexDiv
			flow="column"
			align="flex-start"
			style={{ width: '100%', textAlign: 'initial' }}
			gap="10px"
		>
			<FlexDiv
				gap="8px"
				clickable
				onClick={() => setExportCompanies(!exportCompanies)}
			>
				<BoolCheckbox type="checkbox" checked={exportCompanies} />
				<p style={{ marginBottom: 0 }}>
					<Translate
						id="export_NAME"
						options={{ variables: [['NAME', translateToString('companies')]] }}
					/>
				</p>
			</FlexDiv>
			<FlexDiv
				gap="8px"
				clickable
				onClick={() => setExportProducts(!exportProducts)}
			>
				<BoolCheckbox type="checkbox" checked={exportProducts} />
				<p style={{ marginBottom: 0 }}>
					<Translate
						id="export_NAME"
						options={{ variables: [['NAME', translateToString('general.products')]] }}
					/>
				</p>
			</FlexDiv>
			<DefaultButton
				margin="15px 0 0 0"
				onClick={() => onOk([exportCompanies, exportProducts])}
			>
				<Translate id="export" />
			</DefaultButton>
		</FlexDiv>
	);
}
